import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
const brl = (n) => (Number(n || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const STATUS_STYLE = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  atendido: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelado: "bg-slate-200 text-slate-600 border-slate-300",
};
const STATUS_LABEL = { pendente: "Pendente", atendido: "Atendido", cancelado: "Suspenso" };

export default function PedidosTab() {
  const [list, setList] = useState([]);
  const [itens, setItens] = useState([]);
  const [valorEditing, setValorEditing] = useState(null);
  const [valorInput, setValorInput] = useState("");
  const [savingValor, setSavingValor] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editItemId, setEditItemId] = useState("");
  const [editQtd, setEditQtd] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editObs, setEditObs] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = () => {
    api.get("/pedidos").then(({ data }) => setList(data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/itens").then(({ data }) => setItens(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (pid, status) => {
    try {
      await api.put(`/pedidos/${pid}/status`, { status });
      toast.success("Status atualizado");
      setList(prev => prev.map(p => p.id === pid ? { ...p, status } : p));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditValor = (p) => { setValorEditing(p); setValorInput(String(p.valor_unitario ?? 0)); };
  const saveValor = async () => {
    if (!valorEditing) return;
    const vu = Number(valorInput);
    if (isNaN(vu) || vu < 0) { toast.error("Valor inválido"); return; }
    setSavingValor(true);
    try {
      const { data } = await api.put(`/pedidos/${valorEditing.id}/valor`, { valor_unitario: vu });
      setList(prev => prev.map(p => p.id === valorEditing.id ? { ...p, valor_unitario: data.valor_unitario, valor_total: data.valor_total } : p));
      toast.success("Valor atualizado");
      setValorEditing(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingValor(false); }
  };

  const openEditPedido = (p) => {
    setEditing(p);
    setEditItemId(p.item_id || "");
    setEditQtd(String(p.quantidade ?? ""));
    setEditDesc(p.descricao || "");
    setEditObs(p.observacao || "");
  };
  const saveEdit = async () => {
    if (!editing) return;
    const qtd = Number(editQtd);
    if (!qtd || qtd <= 0) { toast.error("Quantidade deve ser maior que zero"); return; }
    setSavingEdit(true);
    try {
      const payload = {
        quantidade: qtd, descricao: editDesc, observacao: editObs,
      };
      if (editItemId && editItemId !== editing.item_id) payload.item_id = editItemId;
      const { data } = await api.put(`/pedidos/${editing.id}`, payload);
      setList(prev => prev.map(p => p.id === editing.id ? { ...p,
        item_id: data.item_id, item_nome: data.item_nome, unidade: data.unidade,
        valor_unitario: data.valor_unitario,
        quantidade: data.quantidade, valor_total: data.valor_total,
        descricao: data.descricao, observacao: data.observacao,
      } : p));
      toast.success("Pedido atualizado");
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const previewItem = itens.find(i => i.id === editItemId);
  const previewVu = previewItem ? Number(previewItem.valor || 0) : Number(editing?.valor_unitario || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading">Todos os pedidos</CardTitle>
        <CardDescription>{list.length} pedido(s). Você pode editar quantidade, descrição, valor e status de cada pedido.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Solicitante</TableHead>
              <TableHead>Item</TableHead><TableHead>Descrição</TableHead>
              <TableHead className="text-right">Qtd</TableHead>
              <TableHead className="text-right">Valor unit.</TableHead>
              <TableHead className="text-right">Valor total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24">Editar</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Nenhum pedido ainda.</TableCell></TableRow>}
              {list.map((p, i) => {
                const st = p.status || "pendente";
                return (
                  <TableRow key={p.id} className="row-fade" style={{ animationDelay: `${i * 20}ms` }}>
                    <TableCell className="text-sm text-slate-600">{new Date(p.created_at).toLocaleString("pt-BR")}</TableCell>
                    <TableCell className="font-medium">{p.solicitante_nome}</TableCell>
                    <TableCell>{p.item_nome}</TableCell>
                    <TableCell className="text-slate-600">{p.descricao}</TableCell>
                    <TableCell className="text-right font-mono">{p.quantidade} {p.unidade}</TableCell>
                    <TableCell className="text-right">
                      <button type="button" onClick={() => openEditValor(p)}
                        data-testid={`edit-valor-${p.id}`}
                        className="inline-flex items-center gap-1 font-mono hover:text-brand transition-colors"
                        title="Editar valor unitário">
                        {brl(p.valor_unitario)} <Pencil className="h-3 w-3 opacity-60" />
                      </button>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{brl(p.valor_total)}</TableCell>
                    <TableCell>
                      <Select value={st} onValueChange={(v) => changeStatus(p.id, v)}>
                        <SelectTrigger data-testid={`status-select-${p.id}`} className="w-36 h-9">
                          <SelectValue asChild>
                            <Badge variant="outline" className={STATUS_STYLE[st]}>{STATUS_LABEL[st]}</Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="atendido">Atendido</SelectItem>
                          <SelectItem value="cancelado">Suspenso</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => openEditPedido(p)}
                        data-testid={`edit-pedido-${p.id}`} className="rounded-full">
                        <Pencil className="h-3 w-3 mr-1" /> Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Editar valor unitário */}
      <Dialog open={!!valorEditing} onOpenChange={(v) => !v && setValorEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar valor unitário</DialogTitle></DialogHeader>
          {valorEditing && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <div><b>Item:</b> {valorEditing.item_nome}</div>
                <div><b>Quantidade:</b> {valorEditing.quantidade} {valorEditing.unidade}</div>
                <div><b>Solicitante:</b> {valorEditing.solicitante_nome}</div>
              </div>
              <div className="space-y-2">
                <Label>Novo valor unitário (R$)</Label>
                <Input data-testid="edit-valor-input" type="number" min="0" step="0.01"
                  value={valorInput} onChange={(e) => setValorInput(e.target.value)} autoFocus />
                <div className="text-xs text-slate-500">
                  Novo valor total: <b>{brl((Number(valorInput) || 0) * (valorEditing.quantidade || 0))}</b>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setValorEditing(null)}>Cancelar</Button>
            <Button onClick={saveValor} disabled={savingValor} data-testid="edit-valor-save" className="btn-brand">
              {savingValor ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar pedido (item + quantidade + descrição + observação) */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar pedido</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <div><b>Solicitante:</b> {editing.solicitante_nome}</div>
              </div>
              <div className="space-y-2">
                <Label>Item</Label>
                <Select value={editItemId} onValueChange={setEditItemId}>
                  <SelectTrigger data-testid="edit-pedido-item"><SelectValue placeholder="Escolha um item" /></SelectTrigger>
                  <SelectContent>
                    {itens.map(it => (<SelectItem key={it.id} value={it.id}>{it.nome} ({it.unidade})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantidade ({previewItem?.unidade || editing.unidade})</Label>
                <Input data-testid="edit-pedido-qtd" type="number" min="0" step="any"
                  value={editQtd} onChange={(e) => setEditQtd(e.target.value)} />
                <div className="text-xs text-slate-500">
                  Novo valor total: <b>{brl((Number(editQtd) || 0) * previewVu)}</b>
                  {previewItem && previewItem.id !== editing.item_id && (
                    <span className="ml-2 text-brand">(valor unit. atualizado para {brl(previewVu)})</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input data-testid="edit-pedido-desc" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Observação</Label>
                <Textarea data-testid="edit-pedido-obs" rows={2} value={editObs} onChange={(e) => setEditObs(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-pedido-save" className="btn-brand">
              {savingEdit ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
